import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslations } from '../../locales/useTranslations';
import { BiChevronRight, BiLogoInstagram, BiLogoFacebook, BiLogoLinkedin, BiGlobe, BiSolidChevronLeft, BiSolidChevronRight } from "react-icons/bi";

const CustomerCard = ({ imageUrl, altKey, websiteUrl, socialLinks, companyName, companyTypeKey, companyAlt }) => {
    const { t } = useTranslations();
    return (
        <div className="col-lg-6 col-sm-12">
            <div className="single-team-box" role="article">
                <div className="image">
                    <Image
                        src={imageUrl}
                        alt={t(`customerProjects_alt_${altKey}`)}
                        width={200}
                        height={200}
                        style={{ width: '100%', height: 'auto' }}
                        loading="lazy"
                        quality={85}
                    />

                    <ul className="social" role="list" aria-label="Social Media Links">
                        {websiteUrl && (
                            <li>
                                <Link
                                    href={websiteUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={t(`customerProjects_alt_${companyAlt}`)}
                                >
                                    <BiGlobe style={{ color: '#59bd65', paddingLeft: "5px" }} />
                                </Link>
                            </li>
                        )}
                        {socialLinks?.map((social, index) => (
                            <li key={index}>
                                <Link
                                    href={social.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={t(`customerProjects_aria_social_${companyAlt}`)}
                                >
                                    {social.icon}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="content">
                    <h3 className="text-white">{companyName}</h3>
                    <span>{t(`customerProjects_companyTypes_${companyTypeKey}`)}</span>
                </div>
            </div>
        </div>
    );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    const { t } = useTranslations();
    const pages = useMemo(() => {
        const pageNumbers = [];
        for (let i = 1; i <= totalPages; i++) {
            pageNumbers.push(i);
        }
        return pageNumbers;
    }, [totalPages]);

    return (
        <div className="pagination-area text-center">
            <div className="nav-links">
                {currentPage > 1 && (
                    <Link
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onPageChange(currentPage - 1);
                        }}
                        className="prev page-numbers"
                        aria-label={t('customerProjects_aria_prev')}
                    >
                        <BiSolidChevronLeft />
                    </Link>
                )}

                {pages.map(page => (
                    <Link
                        key={page}
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onPageChange(page);
                        }}
                        className={`page-numbers ${currentPage === page ? 'current' : ''}`}
                        aria-label={t('customerProjects_aria_page', { page: page })}
                        aria-current={currentPage === page ? 'page' : undefined}
                    >
                        {page}
                    </Link>
                ))}

                {currentPage < totalPages && (
                    <Link
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onPageChange(currentPage + 1);
                        }}
                        className="next page-numbers"
                        aria-label={t('customerProjects_aria_next')}
                    >
                        <BiSolidChevronRight />
                    </Link>
                )}
            </div>
        </div>
    );
};

const WebentwicklungKundenseiten = () => {
    const { t } = useTranslations();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 4;

    const customers = [
        {
            imageUrl: "/images/web-development/familienart-gmbh.webp",
            altKey: "familienart",
            websiteUrl: "https://familienart.de/",
            companyName: "Familienart.de",
            companyAlt: "familienart",
            companyTypeKey: "gmbh",
            visible: true,
            socialLinks: [
                {
                    platform: "Instagram",
                    url: "https://www.instagram.com/familienart.gmbh/",
                    icon: <BiLogoInstagram style={{ color: '#59bd65', paddingLeft: "5px" }} />
                }
            ]
        },
        {
            imageUrl: "/images/web-development/akgm-verein.webp",
            altKey: "akgm",
            websiteUrl: "https://akgm.com/",
            companyName: "AKGM e. V. c/o Lehrstuhl BWL V",
            companyAlt: "akgm",
            companyTypeKey: "ev",
            visible: true,
            socialLinks: [
                {
                    platform: "Facebook",
                    url: "https://m.facebook.com/AKGMeV/",
                    icon: <BiLogoFacebook style={{ color: '#59bd65', paddingLeft: "5px" }} />
                },
                {
                    platform: "LinkedIn",
                    url: "https://de.linkedin.com/company/arbeitskreis-f%C3%BCr-gesundheits%C3%B6konomie-&--management-e-v--akgm-e-v--",
                    icon: <BiLogoLinkedin style={{ color: '#59bd65', paddingLeft: "5px" }} />
                },
                {
                    platform: "Instagram",
                    url: "https://www.instagram.com/akgmev/",
                    icon: <BiLogoInstagram style={{ color: '#59bd65', paddingLeft: "5px" }} />
                }
            ]
        },
        {
            imageUrl: "/images/web-development/foerde-pfandhaus.webp",
            altKey: "foerde",
            websiteUrl: "https://förde-pfandhaus.de/",
            companyName: "Förde-Pfandhaus",
            companyAlt: "foerde",
            companyTypeKey: "gmbh",
            visible: true,
            socialLinks: []
        },
        {
            imageUrl: "/images/web-development/vfa-international.webp",
            altKey: "vfa",
            websiteUrl: "https://vfa-international.de/",
            companyName: "VfA-International GbR",
            companyAlt: "vfa",
            companyTypeKey: "gbr",
            visible: true,
            socialLinks: [
                {
                    platform: "Facebook",
                    url: "https://www.facebook.com/VfAInternational",
                    icon: <BiLogoFacebook style={{ color: '#59bd65', paddingLeft: "5px" }} />
                }
            ]
        },
        {
            imageUrl: "/images/web-development/retent.webp",
            altKey: "retent",
            websiteUrl: "https://retent.de/",
            companyName: "ReTent GmbH",
            companyAlt: "retent",
            companyTypeKey: "gmbh",
            visible: true,
            socialLinks: []
        },
        {
            imageUrl: "/images/web-development/oldoerp.webp",
            altKey: "oldoerp",
            websiteUrl: "https://oldoerp-immobilien.de",
            companyName: "Sven Oldörp",
            companyAlt: "oldoerp",
            companyTypeKey: "sole",
            visible: true,
            socialLinks: []
        },
    ];

    const totalPages = Math.ceil(customers.filter(customer => customer.visible).length / itemsPerPage);

    const getCurrentPageCustomers = () => {
        const visibleCustomers = customers.filter(customer => customer.visible);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return visibleCustomers.slice(startIndex, endIndex);
    };

    return (
        <section className="case-studies-area pt-100 pb-70">
            <div className="container">
                <div className="section-title text-left">
                    <span className="sub-title">{t('customerProjects_subtitle')}</span>
                    <h2>{t('customerProjects_title')}</h2>
                </div>

                <div className="row">
                    {getCurrentPageCustomers().map((customer, index) => (
                        <CustomerCard
                            key={index}
                            {...customer}
                        />
                    ))}
                </div>

                {totalPages > 1 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>
        </section>
    );
}

export default WebentwicklungKundenseiten;